/**
 * TestDataCard Component
 *
 * Settings card for test data management.
 * Displays test data status and provides removal functionality.
 */

import { Card, BlockStack, Text, Button, Banner, InlineStack, Icon, Box, Collapsible, Divider, List } from '@shopify/polaris';
import { CodeIcon, ChevronUpIcon, ChevronDownIcon, DeleteIcon, AlertTriangleIcon, AlertCircleIcon, InfoIcon } from '@shopify/polaris-icons';
import type { TestDataStatus } from '~/types/test-data';

export interface TestDataCardProps {
  /** Test data status from API */
  status: TestDataStatus | null;
  /** Whether deletion is in progress */
  isDeleting: boolean;
  /** Callback when remove button is clicked */
  onRemove: () => void;
  /** Whether this section is collapsible */
  isCollapsible?: boolean;
  /** Whether the section is open (only used when isCollapsible is true) */
  isOpen?: boolean;
  /** Handler for toggling the section (only used when isCollapsible is true) */
  onToggle?: () => void;
}

/**
 * Get icon for alert severity
 */
function getSeverityIcon(severity: string) {
  switch (severity) {
    case 'critical':
      return AlertTriangleIcon;
    case 'warning':
      return AlertCircleIcon;
    default:
      return InfoIcon;
  }
}

/**
 * Settings card for test data management
 */
export function TestDataCard({
  status,
  isDeleting,
  onRemove,
  isCollapsible = false,
  isOpen = true,
  onToggle,
}: TestDataCardProps) {
  const hasTestData = status?.hasTestData ?? false;
  const productCount = status?.counts?.trackedProducts ?? 0;
  const alertCount = status?.counts?.alerts ?? 0;
  const products = status?.products ?? [];
  const alerts = status?.alerts ?? [];

  const content = (
    <>
      {hasTestData ? (
        <BlockStack gap="400">
          <Banner tone="info">
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                Test data is currently active
              </Text>
              
              {/* Products list */}
              {products.length > 0 && (
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" fontWeight="medium">
                    Test Products ({productCount}):
                  </Text>
                  <List type="bullet">
                    {products.map((product) => (
                      <List.Item key={product.productId}>
                        {product.productTitle}
                      </List.Item>
                    ))}
                    {productCount > products.length && (
                      <List.Item>
                        <Text as="span" tone="subdued">
                          ...and {productCount - products.length} more
                        </Text>
                      </List.Item>
                    )}
                  </List>
                </BlockStack>
              )}

              {/* Alerts list */}
              {alerts.length > 0 && (
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" fontWeight="medium">
                    Test Alerts ({alertCount}):
                  </Text>
                  <List type="bullet">
                    {alerts.map((alert) => (
                      <List.Item key={alert.id}>
                        <InlineStack gap="100" blockAlign="center">
                          <Icon source={getSeverityIcon(alert.severity)} tone={alert.severity === 'critical' ? 'critical' : alert.severity === 'warning' ? 'caution' : 'info'} />
                          <Text as="span">{alert.productTitle}</Text>
                          <Text as="span" tone="subdued">({alert.severity})</Text>
                        </InlineStack>
                      </List.Item>
                    ))}
                    {alertCount > alerts.length && (
                      <List.Item>
                        <Text as="span" tone="subdued">
                          ...and {alertCount - alerts.length} more
                        </Text>
                      </List.Item>
                    )}
                  </List>
                </BlockStack>
              )}
            </BlockStack>
          </Banner>

          <Text as="p" variant="bodySm" tone="subdued">
            Remove test data before using Stock Sentry with real products. This
            action cannot be undone but will not affect your real inventory.
          </Text>
        </BlockStack>
      ) : (
        <Text as="p" variant="bodyMd" tone="subdued">
          No test data present. Your dashboard shows only real product data.
        </Text>
      )}
    </>
  );

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        {isCollapsible ? (
          <div
            role="button"
            tabIndex={0}
            onClick={onToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle?.();
              }
            }}
            aria-expanded={isOpen}
            aria-controls="section-test-data"
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <InlineStack align="space-between" blockAlign="center">
              <InlineStack gap="300" blockAlign="center">
                <Box
                  background="bg-surface-secondary"
                  padding="200"
                  borderRadius="200"
                >
                  <Icon source={CodeIcon} tone="base" />
                </Box>
                <BlockStack gap="050">
                  <Text as="h2" variant="headingMd" fontWeight="semibold">
                    Test Data
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Sample data for testing and evaluation
                  </Text>
                </BlockStack>
              </InlineStack>
              <InlineStack gap="300" blockAlign="center">
                {/* Remove button in header - only show when has test data */}
                {hasTestData && (
                  <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <Button
                      variant="primary"
                      tone="critical"
                      icon={DeleteIcon}
                      onClick={onRemove}
                      loading={isDeleting}
                      disabled={isDeleting}
                    >
                      Remove all test data
                    </Button>
                  </div>
                )}
                <Icon source={isOpen ? ChevronUpIcon : ChevronDownIcon} tone="subdued" />
              </InlineStack>
            </InlineStack>
          </div>
        ) : (
          <InlineStack align="space-between" blockAlign="start">
            <InlineStack gap="300" blockAlign="center">
              <Box
                background="bg-surface-secondary"
                padding="200"
                borderRadius="200"
              >
                <Icon source={CodeIcon} tone="base" />
              </Box>
              <BlockStack gap="050">
                <Text as="h2" variant="headingMd" fontWeight="semibold">
                  Test Data
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Sample data for testing and evaluation
                </Text>
              </BlockStack>
            </InlineStack>
            {/* Remove button in header - only show when has test data */}
            {hasTestData && (
              <Button
                variant="primary"
                tone="critical"
                icon={DeleteIcon}
                onClick={onRemove}
                loading={isDeleting}
                disabled={isDeleting}
              >
                Remove all test data
              </Button>
            )}
          </InlineStack>
        )}

        {/* Content */}
        {isCollapsible ? (
          <Collapsible
            open={isOpen}
            id="section-test-data"
            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
          >
            <BlockStack gap="400">
              <Divider />
              {content}
            </BlockStack>
          </Collapsible>
        ) : (
          content
        )}
      </BlockStack>
    </Card>
  );
}
